import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useFormWithValidation, useProjectSiteParams } from '@/shared/lib';
import { type Contact, useCreateContactMutation, useUpdateContactMutation } from '@/entities/contacts';

import { createContactSchema, DEFAULT_CONTACT_VALUES, type ContactFormValues } from './validation';
import { transformContactToForm, buildCreateRequest, buildUpdateRequest } from '../lib';

interface UseContactFormProps {
  initialData?: Partial<Contact>;
  onSuccess: (contact: Contact) => void;
  onCancel: () => void;
}

export const useContactForm = ({ initialData, onSuccess, onCancel }: UseContactFormProps) => {
  const { t } = useTranslation();
  const { projectId, siteId } = useProjectSiteParams();

  const [createContact, { isLoading: isCreating }] = useCreateContactMutation();
  const [updateContact, { isLoading: isUpdating }] = useUpdateContactMutation();

  const contactSchema = createContactSchema(t);
  const form = useFormWithValidation(contactSchema, DEFAULT_CONTACT_VALUES);

  useEffect(() => {
    if (initialData) {
      const formValues = transformContactToForm(initialData);
      form.reset(formValues);
    }
  }, [initialData, form]);

  const handleSuccess = useCallback(
    (result: Contact) => {
      const messageKey = initialData?.id ? 'contacts.updateSuccess' : 'contacts.createSuccess';
      const defaultMessage = initialData?.id ? 'Contact updated successfully' : 'Contact created successfully';
      toast.success(t(messageKey, defaultMessage));
      onSuccess(result);
    },
    [initialData, onSuccess, t]
  );

  const handleError = useCallback(
    (error: unknown) => {
      toast.error(t('contacts.saveError', 'Failed to save contact'));
      console.error('Failed to save contact:', error);
    },
    [t]
  );

  const onSubmit = useCallback(
    async (values: ContactFormValues) => {
      if (!projectId || !siteId) {
        toast.error(t('common.errors.missingParams', 'Missing required parameters'));
        return;
      }

      try {
        let result: Contact;

        if (initialData?.id) {
          const updateRequest = buildUpdateRequest(values, initialData.id, projectId, siteId);
          result = await updateContact(updateRequest).unwrap();
        } else {
          const createRequest = buildCreateRequest(values, projectId, siteId);
          result = await createContact(createRequest).unwrap();
        }

        handleSuccess(result);
      } catch (error) {
        handleError(error);
      }
    },
    [initialData, projectId, siteId, createContact, updateContact, handleSuccess, handleError, t]
  );

  return {
    form,
    onSubmit: form.handleSubmit(onSubmit),
    isSaving: isCreating || isUpdating,
    onCancel,
    isEditMode: !!initialData?.id
  };
};
